import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@mui/material";
import EnhancedTable from "components/common/EnhancedTable";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SaveIcon from "@mui/icons-material/Save";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { Canvas } from "fabric";

import CertificateTemplateBuilderEditor from "components/certificate-template-builder/CertificateTemplateBuilderEditor";
import {
  getBlankCertificateJson,
  getDemoCertificateJson,
} from "components/certificate-template-builder/templates";
import {
  listCertificateTemplatesFromApi,
  createCertificateTemplate,
  updateCertificateTemplate,
  setPrimaryCertificateTemplate,
  deleteCertificateTemplate,
  getCertificateTemplateById,
  CustomCertificateTemplate,
} from "services/certificateTemplateApiService";
import { useSessionClass } from "contexts/SessionClassContext";
import { useAuth } from "hooks/useAuth";
import { useError } from "contexts/ErrorContext";

const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days draft lifespan

function readDraft(key: string): { canvasJson: string; svgContent: string } | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed._draftSavedAt && Date.now() - parsed._draftSavedAt > DRAFT_TTL_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

type EditorModeNew = { type: "new"; name: string; category: string; starter: "demo" | "blank" };
type EditorModeEdit = { type: "edit"; record: CustomCertificateTemplate };
type EditorMode = EditorModeNew | EditorModeEdit | null;

export default function CertificateTemplateBuilderPage() {
  const { selectedClass, selectedSession } = useSessionClass();
  const { user } = useAuth();
  const { showSuccess, showError, showWarning, showInfo } = useError();

  const classId = selectedClass?.id ?? null;
  const sessionId = selectedSession?.id ?? null;
  const className = selectedClass?.name ?? "";
  const sessionName = selectedSession?.name ?? "";
  const userId = user?.id ?? 1;

  // ── Listing States ────────────────────────────────────────────────────────
  const [templates, setTemplates] = useState<CustomCertificateTemplate[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState("");

  const loadTemplates = useCallback(async () => {
    if (!className || !sessionId) {
      setTemplates([]);
      return;
    }
    setListLoading(true);
    setListError("");
    try {
      const data = await listCertificateTemplatesFromApi(className, sessionId);
      setTemplates(data);
    } catch (err: any) {
      setListError(err?.response?.data?.message || "Failed to load templates.");
    } finally {
      setListLoading(false);
    }
  }, [className, sessionId]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // ── Editor States ──────────────────────────────────────────────────────────
  const [editorMode, setEditorMode] = useState<EditorMode>(null);
  const [editorInitJson, setEditorInitJson] = useState<string | null>(null);
  const [editorKey, setEditorKey] = useState(0);
  const [hasDraftRestored, setHasDraftRestored] = useState(false);
  const fabricCanvasRef = useRef<Canvas | null>(null);
  const [saving, setSaving] = useState(false);
  const [editorLoading, setEditorLoading] = useState(false);

  const handleCanvasReady = useCallback((canvas: Canvas) => {
    fabricCanvasRef.current = canvas;
  }, []);

  // Layout states to pass to auto-save callbacks
  const latestLayoutData = useRef<{ json: string; svg: string }>({ json: "", svg: "" });

  const draftKey = editorMode
    ? editorMode.type === "new"
      ? `cert_draft_new_${className}_${sessionId}_${editorMode.name}`
      : `cert_draft_edit_${editorMode.record.id}`
    : null;

  const handleAutoSave = useCallback((json: string, svg: string) => {
    latestLayoutData.current = { json, svg };
    if (!draftKey) return;
    try {
      localStorage.setItem(
        draftKey,
        JSON.stringify({
          canvasJson: json,
          svgContent: svg,
          _draftSavedAt: Date.now(),
        })
      );
    } catch {}
  }, [draftKey]);

  // ── Dialog States ─────────────────────────────────────────────────────────
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [category, setCategory] = useState("ACADEMIC");
  const [starter, setStarter] = useState<"demo" | "blank">("demo");
  const [createError, setCreateError] = useState("");

  const openCreate = () => {
    setNewName("");
    setCategory("ACADEMIC");
    setStarter("demo");
    setCreateError("");
    setCreateOpen(true);
  };

  const handleCreate = () => {
    const name = newName.trim();
    if (!name) {
      setCreateError("Template name is required.");
      return;
    }

    const newDraftKey = `cert_draft_new_${className}_${sessionId}_${name}`;
    const draft = readDraft(newDraftKey);
    const initJson = draft
      ? draft.canvasJson
      : starter === "demo"
      ? getDemoCertificateJson()
      : getBlankCertificateJson();

    latestLayoutData.current = {
      json: initJson,
      svg: draft ? draft.svgContent : "",
    };

    setEditorInitJson(initJson);
    setHasDraftRestored(!!draft);
    setEditorMode({ type: "new", name, category, starter });
    setEditorKey((k) => k + 1);
    setCreateOpen(false);
  };

  // ── Edit Layout ──────────────────────────────────────────────────────────
  const handleEditTemplate = async (record: CustomCertificateTemplate) => {
    if (!record.id) return;
    const editDraftKey = `cert_draft_edit_${record.id}`;
    const draft = readDraft(editDraftKey);

    if (draft) {
      setEditorInitJson(draft.canvasJson);
      latestLayoutData.current = { json: draft.canvasJson, svg: draft.svgContent };
      setHasDraftRestored(true);
      setEditorMode({ type: "edit", record });
      setEditorKey((k) => k + 1);
      return;
    }

    setEditorLoading(true);
    try {
      const template = await getCertificateTemplateById(record.id);
      setEditorInitJson(template.canvasJson);
      latestLayoutData.current = { json: template.canvasJson, svg: template.svgContent };
      setHasDraftRestored(false);
      setEditorMode({ type: "edit", record });
      setEditorKey((k) => k + 1);
    } catch {
      showError("Failed to load template layout.");
    } finally {
      setEditorLoading(false);
    }
  };

  // ── Save Template ─────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!editorMode) return;
    if (!classId || !sessionId || !className || !sessionName) {
      showWarning("Please select the Class and Session in the header first.");
      return;
    }

    const { json: canvasJson, svg: svgContent } = latestLayoutData.current;
    if (!canvasJson) {
      showWarning("Template layout is empty. Place some elements first.");
      return;
    }

    setSaving(true);
    try {
      if (editorMode.type === "new") {
        await createCertificateTemplate({
          classId,
          className,
          sessionId,
          sessionName,
          templateName: editorMode.name,
          category: editorMode.category,
          canvasJson,
          svgContent,
          primary: false,
          createdBy: userId,
          updatedBy: userId,
        });
        if (draftKey) localStorage.removeItem(draftKey);
        showSuccess(`Template "${editorMode.name}" created successfully.`);
      } else {
        const { record } = editorMode;
        await updateCertificateTemplate({
          ...record,
          className,
          classId,
          sessionName,
          sessionId,
          canvasJson,
          svgContent,
          updatedBy: userId,
        });
        if (draftKey) localStorage.removeItem(draftKey);
        showSuccess(`Template "${record.templateName}" updated successfully.`);
      }
      handleBack();
    } catch (err: any) {
      showError(err?.response?.data?.message || "Failed to save template.");
    } finally {
      setSaving(false);
    }
  };

  // ── Set Primary Default ───────────────────────────────────────────────────
  const [primaryLoading, setPrimaryLoading] = useState<number | null>(null);

  const handleSetPrimary = async (record: CustomCertificateTemplate) => {
    if (!record.id) return;
    setPrimaryLoading(record.id);
    try {
      await setPrimaryCertificateTemplate(record.id);
      await loadTemplates();
      showSuccess(`"${record.templateName}" set as default primary template.`);
    } catch (err: any) {
      showError(err?.response?.data?.message || "Failed to update default template status.");
    } finally {
      setPrimaryLoading(null);
    }
  };

  // ── Delete Template ───────────────────────────────────────────────────────
  const [deleteTarget, setDeleteTarget] = useState<CustomCertificateTemplate | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!deleteTarget || !deleteTarget.id) return;
    setDeleting(true);
    try {
      await deleteCertificateTemplate(deleteTarget.id);
      await loadTemplates();
      setDeleteTarget(null);
      showSuccess("Template deleted successfully.");
    } catch (err: any) {
      showError(err?.response?.data?.message || "Failed to delete template.");
    } finally {
      setDeleting(false);
    }
  };

  // ── Vector SVG High-DPI Print Preview ─────────────────────────────────────
  const handlePreview = () => {
    let svg = latestLayoutData.current.svg;
    if (!svg && fabricCanvasRef.current) {
      svg = fabricCanvasRef.current.toSVG();
    }
    if (!svg) {
      showWarning("Nothing to preview.");
      return;
    }

    // Replace merge fields dynamically with sample data
    let replacedSvg = svg
      .replace(/\{\{student_name\}\}/g, "Johnathan Doe (Sample)")
      .replace(/\{\{roll_no\}\}/g, "Roll No: 1024")
      .replace(/\{\{class_name\}\}/g, className || "Class 10th")
      .replace(/\{\{session_name\}\}/g, sessionName || "2025-26")
      .replace(/\{\{father_name\}\}/g, "Mr. Richard Doe")
      .replace(/\{\{dob\}\}/g, "15/08/2012")
      .replace(/\{\{issue_date\}\}/g, new Date().toLocaleDateString());

    // Open dynamic iframe overlay in new tab for crisp high-resolution printing
    const newTab = window.open("", "_blank");
    if (newTab) {
      newTab.document.write(`
        <html>
          <head>
            <title>Vector Certificate Print Preview</title>
            <style>
              body {
                margin: 0;
                display: flex;
                align-items: center;
                justify-content: center;
                height: 100vh;
                background-color: #f1f5f9;
              }
              svg {
                width: 100%;
                max-width: 842px;
                height: auto;
                box-shadow: 0 10px 30px rgba(0,0,0,0.15);
                background-color: #ffffff;
              }
              @media print {
                body { background-color: #ffffff; }
                svg { box-shadow: none; max-width: 100%; width: 100%; height: 100%; }
              }
            </style>
          </head>
          <body>
            ${replacedSvg}
            <script>
              window.onload = function() {
                // Auto trigger browser print dialog
                setTimeout(function() { window.print(); }, 500);
              }
            </script>
          </body>
        </html>
      `);
      newTab.document.close();
    }
  };

  const handleReset = () => {
    if (draftKey) localStorage.removeItem(draftKey);
    setHasDraftRestored(false);

    if (editorMode?.type === "new") {
      setEditorInitJson(
        editorMode.starter === "blank" ? getBlankCertificateJson() : getDemoCertificateJson()
      );
      setEditorKey((k) => k + 1);
    } else if (editorMode?.type === "edit") {
      handleEditTemplate(editorMode.record);
    }
  };

  const handleBack = () => {
    fabricCanvasRef.current = null;
    setEditorMode(null);
    setHasDraftRestored(false);
    loadTemplates();
  };

  // ── Render Editor Mode ────────────────────────────────────────────────────
  if (editorMode) {
    const templateName =
      editorMode.type === "new" ? editorMode.name : editorMode.record.templateName;
    const isSaved = editorMode.type === "edit";

    return (
      <Box sx={{ display: "flex", flexDirection: "column", height: "calc(100vh - 120px)" }}>
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            borderRadius: 2,
            border: "1px solid rgba(0,0,0,0.08)",
            background: "linear-gradient(135deg,#fff 0%,#f8fafe 100%)",
            mb: 1,
          }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
            <Button variant="text" startIcon={<ArrowBackIcon />} onClick={handleBack} size="small">
              Back to List
            </Button>
            <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" } }} />
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle2" fontWeight={700} lineHeight={1.2}>
                  {templateName}
                </Typography>
                {hasDraftRestored && (
                  <Chip
                    label="Draft restored"
                    size="small"
                    color="warning"
                    variant="outlined"
                    onDelete={() => setHasDraftRestored(false)}
                    sx={{ height: 18, fontSize: 10 }}
                  />
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {isSaved
                  ? `Category: ${editorMode.record.category} · Class: ${editorMode.record.className}`
                  : `New Certificate Layout · Category: ${editorMode.category}`}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                variant="outlined"
                startIcon={<RemoveRedEyeIcon />}
                onClick={handlePreview}
                size="small"
              >
                Print Preview
              </Button>
              <Button
                variant="contained"
                startIcon={
                  saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />
                }
                onClick={handleSave}
                disabled={saving || !classId || !sessionId}
                size="small"
                color={isSaved ? "success" : "primary"}
              >
                {saving ? "Saving…" : isSaved ? "Update" : "Save Designer"}
              </Button>
              <Button
                variant="text"
                color="inherit"
                startIcon={<RestartAltIcon />}
                onClick={handleReset}
                size="small"
              >
                Reset
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            flex: 1,
            borderRadius: 2,
            border: "1px solid rgba(0,0,0,0.08)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <CertificateTemplateBuilderEditor
            key={`${editorKey}`}
            editorKey={`${editorKey}`}
            initialJson={editorInitJson}
            onAutoSave={handleAutoSave}
            onCanvasReady={handleCanvasReady}
          />
        </Paper>
      </Box>
    );
  }

  // ── Render List Dashboard Mode ───────────────────────────────────────────
  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: 2,
          border: "1px solid rgba(0,0,0,0.08)",
          background: "linear-gradient(135deg,#fff 0%,#f8fafe 100%)",
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={800}>
              Certificate Template Builder
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {classId && sessionId
                ? `Active Templates for class: ${className} · Session: ${sessionName}`
                : "Select the Class and Session in the header to view and construct certificates."}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
            disabled={!classId || !sessionId}
          >
            Create New Template
          </Button>
        </Stack>
      </Paper>

      {(!classId || !sessionId) && (
        <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
          Please select a Class and Active Session from the header to manage certificates.
        </Alert>
      )}

      <Box sx={{ mt: 2 }}>
        {listError ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={loadTemplates}>
                Retry
              </Button>
            }
          >
            {listError}
          </Alert>
        ) : (
          <EnhancedTable
            columns={[
              { id: "templateName", label: "Template Name", minWidth: 150 },
              { id: "category", label: "Category", minWidth: 100 },
              { id: "className", label: "Class", minWidth: 100 },
              { id: "sessionName", label: "Session", minWidth: 100 },
              {
                id: "primary",
                label: "Primary",
                minWidth: 80,
                format: (value: boolean) =>
                  value ? (
                    <Chip
                      label="Primary"
                      size="small"
                      color="success"
                      icon={<StarIcon sx={{ fontSize: "14px !important" }} />}
                    />
                  ) : (
                    <Typography variant="caption" color="text.disabled">—</Typography>
                  ),
              },
            ]}
            data={templates}
            loading={listLoading}
            searchFields={["templateName", "className", "category"]}
            actions={(row: CustomCertificateTemplate) => (
              <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                <Tooltip title="Edit Layout">
                  <IconButton
                    size="small"
                    onClick={() => handleEditTemplate(row)}
                    disabled={editorLoading}
                  >
                    {editorLoading ? <CircularProgress size={16} /> : <EditIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title={row.primary ? "Primary default" : "Set as default template"}
                >
                  <IconButton
                    size="small"
                    onClick={() => handleSetPrimary(row)}
                    disabled={primaryLoading !== null}
                  >
                    {primaryLoading === row.id ? (
                      <CircularProgress size={16} />
                    ) : row.primary ? (
                      <StarIcon fontSize="small" sx={{ color: "#f59e0b" }} />
                    ) : (
                      <StarBorderIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" color="error" onClick={() => setDeleteTarget(row)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}
            cardProps={{ elevation: 0, sx: { border: "none", boxShadow: "none", backgroundColor: "transparent" } }}
          />
        )}
      </Box>

      {/* ── Create Dialog ── */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New Certificate Template</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {createError && <Alert severity="error">{createError}</Alert>}
            <TextField
              label="Template Name"
              value={newName}
              onChange={(e) => {
                setNewName(e.target.value);
                setCreateError("");
              }}
              placeholder="e.g. Sports Excellence Certificate"
              fullWidth
              autoFocus
              required
              size="small"
            />

            <FormControl size="small" fullWidth>
              <InputLabel>Certificate Category</InputLabel>
              <Select
                value={category}
                label="Certificate Category"
                onChange={(e) => setCategory(e.target.value)}
              >
                <MenuItem value="ACADEMIC">Academic / Education</MenuItem>
                <MenuItem value="SPORTS">Sports Excellence</MenuItem>
                <MenuItem value="EVENT">School Event / Competition</MenuItem>
                <MenuItem value="TC">Transfer Certificate (TC)</MenuItem>
                <MenuItem value="APPRECIATION">Appreciation</MenuItem>
                <MenuItem value="RECOMMENDATION">Letter of Recommendation</MenuItem>
              </Select>
            </FormControl>

            <Box>
              <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                Start Designer Canvas With
              </Typography>
              <RadioGroup
                value={starter}
                onChange={(e) => setStarter(e.target.value as "demo" | "blank")}
              >
                <FormControlLabel
                  value="demo"
                  control={<Radio size="small" />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>Demo pre-styled template</Typography>
                      <Typography variant="caption" color="text.secondary">
                        A4 Landscape with gold seals, header lines, and placeholders preloaded.
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="blank"
                  control={<Radio size="small" />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>Blank canvas layout</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Clean empty board with double geometric borders only.
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate}>
            Open Designer Canvas
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirmation Dialog ── */}
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Template</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to permanently delete <strong>{deleteTarget?.templateName}</strong>?
            This will wipe the design from the database and cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
            Permanently Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
